import { useEffect, useState } from "react";
import { Headphones } from "lucide-react";
import {
  callNextTicket,
  callTicketById,
  subscribeToCalls,
  subscribeToWaitingTickets,
  QueueCall,
  QueueTicket,
} from "../../../services/queueService";
import { showToast } from "../../../utils/toast";

interface Props {
  userId: string;
}

const formatEspera = (createdAt?: Date) => {
  if (!createdAt) return "";
  const minutos = Math.max(0, Math.round((Date.now() - createdAt.getTime()) / 60000));
  return minutos <= 0 ? "agora" : `${minutos} min`;
};

const ControleAtendete = ({ userId }: Props) => {
  const [deskId, setDeskId] = useState("recepção");
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [lastCall, setLastCall] = useState<QueueCall | null>(null);
  const [waiting, setWaiting] = useState<QueueTicket[]>([]);

  useEffect(() => {
    if (!userId) return;
    const unsubs = [
      subscribeToCalls(1, userId, (calls) => setLastCall(calls[0] ?? null)),
      subscribeToWaitingTickets(userId, setWaiting),
    ];
    return () => unsubs.forEach((fn) => fn());
  }, [userId]);

  const normalCount = waiting.filter((t) => t.type === "normal").length;
  const preferentialCount = waiting.filter((t) => t.type === "preferential").length;

  const handleCallNext = async (type: "normal" | "preferential") => {
    try {
      setLoadingKey(`next-${type}`);
      const ticket = await callNextTicket(deskId, userId, type);
      if (!ticket) { showToast.info("Nenhuma senha aguardando nesse tipo"); return; }
      showToast.success(`Chamando ${ticket.type === "preferential" ? "R" : "E"}-${ticket.number} → ${deskId}`);
    } catch (err: any) {
      const msg = err?.message ?? "";
      if (msg.includes("index") || msg.includes("Index")) {
        showToast.error("Índice do banco ainda em construção. Aguarde 1–2 minutos e tente novamente.");
      } else {
        showToast.error("Erro ao chamar próxima senha");
      }
    } finally {
      setLoadingKey(null);
    }
  };

  const handleCallSpecific = async (ticket: QueueTicket) => {
    try {
      setLoadingKey(ticket.id);
      const called = await callTicketById(ticket.id, deskId, userId);
      if (!called) { showToast.error("Essa senha não está mais disponível"); return; }
      showToast.success(`Chamando ${called.type === "preferential" ? "R" : "E"}-${called.number} → ${deskId}`);
    } catch {
      showToast.error("Erro ao chamar senha");
    } finally {
      setLoadingKey(null);
    }
  };

  const busy = loadingKey !== null;

  return (
    <div className="painel-card">
      <div className="painel-card-header">
        <div className="painel-card-icon"><Headphones size={20} /></div>
        <div>
          <p className="painel-card-title">Controle do Atendente</p>
          <p className="painel-card-subtitle">Fila em espera</p>
        </div>
      </div>

      <div className="painel-stats">
        <div className="painel-stat normal">
          <div className="painel-stat-value">{normalCount}</div>
          <div className="painel-stat-label">Escolher</div>
        </div>
        <div className="painel-stat preferential">
          <div className="painel-stat-value">{preferentialCount}</div>
          <div className="painel-stat-label">Retirada</div>
        </div>
      </div>

      <input
        className="painel-select"
        value={deskId}
        onChange={(e) => setDeskId(e.target.value)}
        placeholder="Ex: Guichê 01, Caixa 2..."
      />

      <div className="painel-actions" style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
        <button
          className="painel-btn painel-btn-retirada"
          onClick={() => handleCallNext("preferential")}
          disabled={busy || !deskId}
          style={{ flex: 1 }}
        >
          {loadingKey === "next-preferential" ? "Chamando…" : "Próxima Retirada"}
        </button>
        <button
          className="painel-btn painel-btn-entrega"
          onClick={() => handleCallNext("normal")}
          disabled={busy || !deskId}
          style={{ flex: 1 }}
        >
          {loadingKey === "next-normal" ? "Chamando…" : "Próxima Escolher"}
        </button>
      </div>

      {lastCall && (
        <div className="painel-result">
          <span>Última chamada</span>
          <strong className="text-uppercase">{lastCall.display} — {lastCall.deskId}</strong>
        </div>
      )}

      {waiting.length > 0 && (
        <div style={{ marginTop: "12px", borderTop: "1px solid rgba(0,0,0,0.08)", paddingTop: "12px" }}>
          <p className="painel-card-subtitle" style={{ marginBottom: "8px" }}>
            Fila de espera — escolha uma senha específica para chamar fora da ordem
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "220px", overflowY: "auto" }}>
            {waiting.map((ticket) => (
              <div
                key={ticket.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  padding: "6px 10px",
                  borderRadius: "8px",
                  background: "rgba(0,0,0,0.04)",
                }}
              >
                <span>
                  <strong>{ticket.type === "preferential" ? "R" : "E"}-{ticket.number}</strong>
                  {" "}
                  <span style={{ opacity: 0.7, fontSize: "0.85em" }}>
                    ({ticket.type === "preferential" ? "Retirada" : "Escolher"} · {formatEspera(ticket.createdAt)})
                  </span>
                </span>
                <button
                  className="painel-btn painel-btn-ghost"
                  onClick={() => handleCallSpecific(ticket)}
                  disabled={busy || !deskId}
                  style={{ padding: "4px 12px" }}
                >
                  {loadingKey === ticket.id ? "Chamando…" : "Chamar"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ControleAtendete;
